import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  OverturAdminSubDetails,
  OverturAdminSubHistory,
} from '../../../interfaces/overtur-admin-subs';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';

import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { debounceTime, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-overtursub-details-page',
  templateUrl: './overtursub-details-page.component.html',
  styleUrls: ['./overtursub-details-page.component.scss'],

  standalone: false,
})
/* istanbul ignore next */
export class OvertursubDetailsPageComponent {
  subscriptionDetails!: OverturAdminSubDetails;
  subscriptionHistory: OverturAdminSubHistory[] = [];
  displayedColumns: string[] = [
    'endUserEmail',
    'sku',
    'poNumber',
    'soNumber',
    'invoiceNumber',
    'amount',
    'startDate',
    'endDate',
    'subscriptionStatus',
  ];
  isEditingEmail: boolean = false;
  isEditingRenewalDate: boolean = false;
  newEmail: string = '';
  newRenewalDate: Date | string = '';
  renewalDateChangeReason: string = '';
  emailValidationError: string | null = null;
  isEmailValid: boolean = true;
  emailValidationErrors: string[] = [];
  newCompanyName: string = '';
  newCompanyAccountId: string = '';
  originalCompanyName: string = '';
  originalCompanyAccountId: string = '';
  minRenewalDate: Date = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  updateError: string | null = null;
  emailChange$ = new Subject<string>();
  emailChangeSub?: Subscription;
  EMAIL_DEBOUNCE_MS = 600;
  isEmailValidating = false;

  constructor(
    private readonly backend: BackendAccessService,
    private readonly route: ActivatedRoute,
    private readonly loading: LoadingService
  ) {}
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<OverturAdminSubHistory>();
  /* istanbul ignore next */
  ngOnInit(): void {
    this.loadSubscriptionDetails();

    this.emailChangeSub = this.emailChange$
      .pipe(debounceTime(this.EMAIL_DEBOUNCE_MS))
      .subscribe(() => {
        // Only validate if the email actually changed from the original
        if (this.emailChanged()) {
          this.validateEmail();
        } else {
          // Reset validation state if unchanged
          this.isEmailValid = true;
          this.emailValidationError = null;
          this.emailValidationErrors = [];
          this.isEmailValidating = false;
        }
      });
  }
  /* istanbul ignore next */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /* istanbul ignore next */
  loadSubscriptionDetails() {
    this.loading.show();
    this.route.queryParamMap.subscribe((params) => {
      const orid = params.get('orid');
      const subid = params.get('subid');

      if (orid) {
        this.backend
          .getOverturSubscriptionDetails({ orid, subid: subid ?? undefined })
          .subscribe({
            next: (response) => {
              this.subscriptionDetails = response.currentSubscriptionDetails;
              this.subscriptionHistory = response.subscriptionHistory;
              this.originalCompanyName =
                this.subscriptionDetails.endUserCompanyName || '';
              this.originalCompanyAccountId =
                this.subscriptionDetails.endUserCompanyAccountId || '';
              this.newCompanyName = '';
              this.newCompanyAccountId = '';
              this.dataSource.data = this.subscriptionHistory;

              // Sync edited fields with loaded data
              this.newEmail = this.subscriptionDetails.endUserEmail || '';
              this.newRenewalDate = this.subscriptionDetails.renewalDate || '';
              this.renewalDateChangeReason = '';

              this.loading.hide();
            },
            error: (err) => {
              console.error('Error fetching subscription details:', err);
              this.loading.hide();
            },
          });
      } else {
        console.error('Missing required orid query param');
        this.loading.hide();
      }
    });
  }
  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.emailChangeSub?.unsubscribe();
  }
  /* istanbul ignore next */
  onEmailModelChange(value: string) {
    // Update model (ngModel already does that), mark as not validated while typing
    // If the value is same as original, we consider it valid immediately
    if (this.emailChanged()) {
      this.isEmailValid = false;
      this.emailValidationError = null;
      this.emailValidationErrors = [];
    } else {
      this.isEmailValid = true;
      this.emailValidationError = null;
      this.emailValidationErrors = [];
    }

    // push value for debounce stream
    this.emailChange$.next(value);
  }

  /* istanbul ignore next */
  toggleEmailEdit() {
    this.isEditingEmail = !this.isEditingEmail;
    if (this.isEditingEmail) {
      this.newEmail = this.subscriptionDetails.endUserEmail || '';
    }
  }

  /* istanbul ignore next */
  toggleRenewalDateEdit() {
    this.isEditingRenewalDate = !this.isEditingRenewalDate;
    if (this.isEditingRenewalDate) {
      this.newRenewalDate = this.subscriptionDetails.renewalDate || '';
    }
  }

  /* istanbul ignore next */
  saveEmail() {
    this.subscriptionDetails.endUserEmail = this.newEmail;
    this.isEditingEmail = false;
    // Optionally, call your backend API to persist the change
  }

  /* istanbul ignore next */
  saveRenewalDate() {
    this.subscriptionDetails.renewalDate =
      typeof this.newRenewalDate === 'string'
        ? new Date(this.newRenewalDate)
        : this.newRenewalDate;
    this.isEditingRenewalDate = false;
    // Optionally, call your backend API to persist the change
  }

  /* istanbul ignore next */
  cancelEmailEdit() {
    this.isEditingEmail = false;
    this.newEmail = this.subscriptionDetails?.endUserEmail ?? '';
    this.isEmailValid = true;
    this.emailValidationError = null;
    this.emailValidationErrors = [];
    this.newCompanyName = '';
    this.newCompanyAccountId = '';
  }
  /* istanbul ignore next */
  validateEmail() {
    this.isEmailValidating = true;
    this.loading.show();
    if (!this.emailChanged()) {
      this.isEmailValid = true;
      this.emailValidationError = null;
      this.emailValidationErrors = [];
      this.loading.hide();
      return;
    }

    if (!this.newEmail) {
      this.emailValidationError = 'Email is required.';
      this.isEmailValid = false;
      this.emailValidationErrors = [];
      this.loading.hide();
      return;
    }

    this.backend.getOverturEmailValidation(this.newEmail).subscribe({
      next: (response) => {
        if (response.isValid) {
          this.isEmailValidating = false;
          this.isEmailValid = true;
          this.emailValidationError = null;
          this.emailValidationErrors = [];
          if (response.endUserCompanyDetails) {
            this.newCompanyName =
              response.endUserCompanyDetails.businessId || '';
            this.newCompanyAccountId =
              response.endUserCompanyDetails.accountId || '';
          } else {
            this.newCompanyName = '';
            this.newCompanyAccountId = '';
          }
        } else {
          this.isEmailValid = false;
          this.emailValidationError = 'Invalid email address.';
          this.emailValidationErrors = response.errors || [];
          this.newCompanyName = '';
          this.newCompanyAccountId = '';
        }
        this.loading.hide();
      },
      error: (err) => {
        this.isEmailValidating = false;
        this.isEmailValid = false;
        this.emailValidationError = 'Error validating email.';
        this.emailValidationErrors = [];
        this.newCompanyName = '';
        this.newCompanyAccountId = '';
        console.error('Email validation error:', err);
        this.loading.hide();
      },
    });
  }
  /* istanbul ignore next */
  emailChanged(): boolean {
    return this.newEmail !== this.subscriptionDetails.endUserEmail;
  }

  /* istanbul ignore next */
  renewalDateChanged(): boolean {
    if (!this.subscriptionDetails || !this.newRenewalDate) {
      return false;
    }
    const newDate =
      typeof this.newRenewalDate === 'string'
        ? new Date(this.newRenewalDate)
        : this.newRenewalDate;
    const originalDate = new Date(this.subscriptionDetails.renewalDate);
    return newDate.getTime() !== originalDate.getTime();
  }

  /* istanbul ignore next */
  cancelRenewalDateEdit() {
    this.isEditingRenewalDate = false;
  }

  /* istanbul ignore next */
  get hasChanges(): boolean {
    if (!this.subscriptionDetails) {
      return false;
    }

    const emailChanged =
      this.newEmail !== this.subscriptionDetails.endUserEmail;

    let renewalDateChanged = false;
    if (this.newRenewalDate && this.subscriptionDetails.renewalDate) {
      const newDate =
        typeof this.newRenewalDate === 'string'
          ? new Date(this.newRenewalDate)
          : this.newRenewalDate;
      const originalDate = new Date(this.subscriptionDetails.renewalDate);
      renewalDateChanged = newDate.getTime() !== originalDate.getTime();
    }

    // Validate email only if changed
    const emailValid = emailChanged ? this.isEmailValid : true;

    // Validate reason only if renewal date changed
    const reasonValid = renewalDateChanged ? this.isReasonValid : true;

    return (emailChanged || renewalDateChanged) && emailValid && reasonValid;
  }

  get isReasonValid(): boolean {
    // If renewal date has changed, reason must be non-empty
    if (this.renewalDateChanged()) {
      return this.renewalDateChangeReason.trim().length > 0;
    }
    // Otherwise, reason is not required
    return true;
  }

  /* istanbul ignore next */
  updateOverturSubscription() {
    const payload = {
      trialRequestId: this.subscriptionDetails.trialRequestId,
      subscriptionId: this.subscriptionDetails.subscriptionId,
      orderId: this.subscriptionDetails.orderId,
      newEndUserEmail: this.newEmail || this.subscriptionDetails.endUserEmail,
      newCompanyName:
        this.newCompanyName || this.subscriptionDetails.endUserCompanyName,
      newCompanyAccountId:
        this.newCompanyAccountId ||
        this.subscriptionDetails.endUserCompanyAccountId,
      newRenewalDate:
        typeof this.newRenewalDate === 'string'
          ? new Date(this.newRenewalDate)
          : this.newRenewalDate || this.subscriptionDetails.renewalDate,
      renewalDateNotes:
        this.renewalDateChangeReason ||
        this.subscriptionDetails.renewalDateNotes,
    };

    this.backend.updateOverturAdminSubs(payload).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.updateError = null;
          console.log('Subscription updated successfully');
          if (this.newCompanyName) {
            this.originalCompanyName = this.newCompanyName;
            this.originalCompanyAccountId = this.newCompanyAccountId;
          }
          this.loadSubscriptionDetails();
          this.cancelEmailEdit();
          this.cancelRenewalDateEdit();
          this.renewalDateChangeReason = '';
        } else {
          this.loading.hide();
          console.error('Update failed:', response.errors);
          this.updateError =
            response.errors ||
            'Update failed. Please check the details and try again.';
        }
      },
      error: (err) => {
        console.error('Error updating subscription:', err);
      },
    });
  }
}
